import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Prompt, useHistory, useLocation } from 'react-router-dom'
import Add from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import API from '../../component/Axios'
import BottomButtonGroup, {
    FORM_IMAGE_CLEAR_BUTTON_SX,
    FORM_WIDE_UPLOAD_BUTTON_SX,
} from '../../component/BottomButtonGroup'
import ListEmptyState from '../../component/ListEmptyState'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import Title from '../../component/Title'
import { FORM_IMAGE_PREVIEW_FRAME_SX, FORM_PANEL_SX } from '../../component/formSurfaceStyles'
import { createListLinkTarget, getLocationState } from '../../utils/listPageState'

const LIST_PATH = '/appTerritorySkins'
const IMAGE_FILE_ACCEPT = 'image/png,image/jpeg,image/jpg,image/gif,image/webp'
const LEAVE_MESSAGE = '저장하지 않은 땅따먹기 스킨 변경사항이 있습니다. 이동하시겠습니까?'

const createDefaultParams = (type = 'color') => ({
    territorySkinId: '',
    type,
    skinKey: '',
    color: '#8A8A8A',
    previewImageUrl: '',
    previewImage: '',
    pricePoint: 0,
    sort: 0,
    status: 0,
})

const IMAGE_PANEL_SX = FORM_PANEL_SX

const IMAGE_PREVIEW_FRAME_SX = {
    ...FORM_IMAGE_PREVIEW_FRAME_SX,
    height: { xs: 180, md: 220 },
}

const IMAGE_PREVIEW_SX = {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
}

const COLOR_SWATCH_SX = {
    width: 44,
    height: 34,
    border: '1px solid rgba(148, 163, 184, 0.45)',
    borderRadius: '4px',
    flexShrink: 0,
}

const getFileName = (file) => {
    if (!file || typeof file !== 'object') {
        return ''
    }

    return file.name || ''
}

const isImageFile = (file) => {
    if (!file) {
        return false
    }

    return IMAGE_FILE_ACCEPT.split(',').includes(file.type)
}

const normalizeHexColor = (value) => {
    const color = String(value || '').trim()
    return /^#[0-9a-fA-F]{6}$/.test(color) ? color.toUpperCase() : ''
}

function PreviewImagePanel({
    previewImageSrc,
    previewImageFile,
    previewImageUrl,
    disabled,
    onUpload,
    onClearFile,
}) {
    const fileInputRef = useRef(null)
    const fileName = getFileName(previewImageFile)

    const handleClearClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }

        onClearFile?.()
    }

    return (
        <Paper variant="outlined" sx={IMAGE_PANEL_SX}>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        미리보기 이미지
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        이미지 스킨 미리보기에 사용할 파일을 업로드합니다.
                    </Typography>
                </Box>
                {previewImageFile instanceof File && (
                    <Button
                        size="small"
                        color="error"
                        variant="text"
                        startIcon={<RemoveIcon />}
                        onClick={handleClearClick}
                        sx={FORM_IMAGE_CLEAR_BUTTON_SX}
                        disabled={disabled}
                    >
                        선택 취소
                    </Button>
                )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={IMAGE_PREVIEW_FRAME_SX}>
                    <Box
                        component="img"
                        src={previewImageSrc || previewImageUrl || '/images/noimage.jpg'}
                        alt="스킨 이미지 미리보기"
                        sx={IMAGE_PREVIEW_SX}
                    />
                </Box>
                <Box>
                    <FormControl fullWidth>
                        <TextField
                            inputProps={{ accept: IMAGE_FILE_ACCEPT }}
                            inputRef={fileInputRef}
                            style={{ display: 'none' }}
                            name="previewImage"
                            id="territory-preview-image"
                            type="file"
                            disabled={disabled}
                            onChange={onUpload}
                        />
                        <Box component="label" htmlFor="territory-preview-image" sx={{ display: 'block' }}>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                component="span"
                                startIcon={<Add />}
                                sx={FORM_WIDE_UPLOAD_BUTTON_SX}
                                disabled={disabled}
                            >
                                이미지 선택
                            </Button>
                        </Box>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {fileName ? `선택한 파일: ${fileName}` : '등록 시 이미지를 선택해주세요.'}
                    </Typography>
                    {previewImageUrl && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}
                    >
                        저장된 URL: {previewImageUrl}
                    </Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    )
}

export default function TerritorySkinFormPage({
    mode,
    routeType,
    territorySkinId,
}) {
    const history = useHistory()
    const location = useLocation()
    const locationState = getLocationState(location)
    const curPage = locationState.curPage || 1
    const searchText = locationState.searchText || ''
    const searchSelect = locationState.searchSelect || {}
    const listSearch = location.search || ''
    const isEditMode = mode === 'edit'
    const [detailLoading, setDetailLoading] = useState(isEditMode)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState(() => createDefaultParams(routeType || 'color'))
    const [previewImageSrc, setPreviewImageSrc] = useState('')
    const [detailErrorMessage, setDetailErrorMessage] = useState('')
    const [openSnackbar, setOpenSnackbar] = useState(false)
    const [severitySnackbar, setSeveritySnackbar] = useState('info')
    const [messageSnackbar, setMessageSnackbar] = useState('')
    const [navigationUnlocked, setNavigationUnlocked] = useState(false)
    const initialSnapshotRef = useRef(JSON.stringify(createDefaultParams(routeType || 'color')))

    const createSnapshot = useCallback((nextParams = params) => {
        const snapshotParams = {
            ...nextParams,
            previewImage: nextParams.previewImage instanceof File ? nextParams.previewImage.name : '',
        }

        return JSON.stringify(snapshotParams)
    }, [params])

    const isDirty = createSnapshot() !== initialSnapshotRef.current
    const leaveGuardEnabled = isDirty && !saving && !navigationUnlocked

    useEffect(() => {
        if (!isEditMode) {
            return
        }

        const getData = async () => {
            if (!territorySkinId || !routeType) {
                const errorMessage = '필수 파라미터가 없어 땅따먹기 스킨 상세를 불러올 수 없습니다.'
                setDetailErrorMessage(errorMessage)
                setDetailLoading(false)
                return
            }

            setDetailLoading(true)
            setDetailErrorMessage('')

            try {
                const { data } = await API.post('/api/v1/admin/territorySkin/getDetail', {
                    territorySkinId,
                    type: routeType,
                })

                if (data.code === 200) {
                    if (!data.data) {
                        setDetailErrorMessage('땅따먹기 스킨 상세 정보를 찾을 수 없습니다.')
                        setDetailLoading(false)
                        return
                    }

                    const detailParams = {
                        ...createDefaultParams(data.data.type),
                        ...data.data,
                        previewImage: '',
                        color: data.data.color || '#8A8A8A',
                        pricePoint: Number(data.data.pricePoint || 0),
                        sort: Number(data.data.sort || 0),
                        status: Number(data.data.status || 0),
                    }

                    setParams(detailParams)
                    setPreviewImageSrc('')
                    initialSnapshotRef.current = JSON.stringify({
                        ...detailParams,
                        previewImage: '',
                    })
                    setDetailErrorMessage('')
                } else if (data.code === 403) {
                    history.push('/login')
                    return
                } else {
                    const errorMessage = data.message || '땅따먹기 스킨 상세를 불러오지 못했습니다.'
                    setDetailErrorMessage(errorMessage)
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(errorMessage)
                }
            } catch (_error) {
                const errorMessage = '땅따먹기 스킨 상세를 불러오지 못했습니다.'
                setDetailErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            } finally {
                setDetailLoading(false)
            }
        }

        getData()
    }, [history, isEditMode, routeType, territorySkinId])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const moveToList = (snackbarState) => {
        setNavigationUnlocked(true)

        setTimeout(() => {
            history.push(createListLinkTarget({
                pathname: LIST_PATH,
                search: listSearch,
                ...(snackbarState ? {
                    openSnackbar: true,
                    severitySnackbar: snackbarState.severity,
                    messageSnackbar: snackbarState.message,
                } : {}),
                curPage,
                searchText,
                searchSelect,
            }))
        }, 0)
    }

    const handleBackClick = (event) => {
        if (!leaveGuardEnabled) {
            return
        }

        event.preventDefault()

        if (window.confirm(LEAVE_MESSAGE)) {
            moveToList()
        }
    }

    const handleUpdateField = (e) => {
        const { name, value } = e.target

        setParams(prevParams => ({
            ...prevParams,
            [name]: value,
        }))
    }

    const handleTypeChange = (e) => {
        const type = e.target.value

        setParams(prevParams => ({
            ...createDefaultParams(type),
            color: prevParams.color || '#8A8A8A',
            sort: prevParams.sort,
            status: prevParams.status,
        }))
        setPreviewImageSrc('')
    }

    const handleUploadClick = (e) => {
        const file = e.target.files?.[0]

        if (!file) {
            return
        }

        if (!isImageFile(file)) {
            e.target.value = ''
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('JPG, PNG, GIF, WEBP 형식의 이미지만 업로드할 수 있습니다.')
            return
        }

        const reader = new FileReader()

        reader.onloadend = () => {
            setParams(prevParams => ({
                ...prevParams,
                previewImage: file,
            }))
            setPreviewImageSrc(reader.result)
        }

        reader.readAsDataURL(file)
    }

    const handleClearSelectedImage = () => {
        setParams(prevParams => ({
            ...prevParams,
            previewImage: '',
        }))
        setPreviewImageSrc('')
    }

    const validateParams = () => {
        const color = normalizeHexColor(params.color)
        const sort = Number(params.sort)
        const status = Number(params.status)
        const pricePoint = Number(params.pricePoint)
        const previewImageUrl = String(params.previewImageUrl || '').trim()

        if (!color || !Number.isInteger(sort) || sort < 0 || ![0, 1].includes(status)) {
            return { ok: false, message: '필수 입력값을 확인해주세요.' }
        }

        if (params.type === 'image') {
            if (!Number.isInteger(pricePoint) || pricePoint < 0) {
                return { ok: false, message: '판매 포인트를 확인해주세요.' }
            }

            if (!(params.previewImage instanceof File) && !previewImageUrl) {
                return { ok: false, message: '이미지를 업로드해주세요.' }
            }
        }

        return {
            ok: true,
            data: {
                color,
                sort,
                status,
                pricePoint,
                previewImageUrl,
            },
        }
    }

    const saveData = async (formData) => {
        try {
            setSaving(true)

            const { data } = await API({
                method: 'post',
                url: isEditMode ? '/api/v1/admin/territorySkin/update' : '/api/v1/admin/territorySkin/create',
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            })

            if (data.code === 200) {
                moveToList({
                    severity: 'success',
                    message: data.message || 'success',
                })
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '땅따먹기 스킨을 저장하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('땅따먹기 스킨을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()

        if (saving) {
            return
        }

        const validation = validateParams()

        if (!validation.ok) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(validation.message)
            return
        }

        const formData = new FormData()
        if (isEditMode) {
            formData.append('territorySkinId', territorySkinId)
        }
        formData.append('type', params.type)
        formData.append('color', validation.data.color)
        formData.append('sort', validation.data.sort)
        formData.append('status', validation.data.status)

        if (params.type === 'image') {
            if (validation.data.previewImageUrl) {
                formData.append('previewImageUrl', validation.data.previewImageUrl)
            }
            formData.append('pricePoint', validation.data.pricePoint)
            if (params.previewImage instanceof File) {
                formData.append('previewImage', params.previewImage)
            }
        }

        saveData(formData)
    }

    const renderDetailErrorState = () => (
        <ListEmptyState
            variant="error"
            title="땅따먹기 스킨 상세를 불러오지 못했습니다."
            description={detailErrorMessage || '목록으로 돌아가 다시 확인하세요.'}
            action={<BottomButtonGroup variant="emptyStateAction" onBackClick={() => moveToList()} backLabel="목록으로" />}
            sx={{ mt: 2 }}
        />
    )

    if (detailLoading) {
        return (
            <Grid item xs={12}>
                <Spiner/>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Grid>
        )
    }

    if (isEditMode && detailErrorMessage) {
        return (
            <Grid item xs={12}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    <Title>APP 땅따먹기 스킨 수정</Title>
                    {renderDetailErrorState()}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            </Grid>
        )
    }

    return (
        <Grid item xs={12}>
            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                <Title>{isEditMode ? 'APP 땅따먹기 스킨 수정' : 'APP 땅따먹기 스킨 등록'}</Title>
                <Prompt when={leaveGuardEnabled} message={LEAVE_MESSAGE} />
                <Box component="form" encType="multipart/form-data" onSubmit={handleSubmit} sx={{ opacity: saving ? 0.6 : 1 }}>
                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="타입"
                                    select
                                    id="type"
                                    name="type"
                                    value={params.type}
                                    size="small"
                                    disabled={saving || isEditMode}
                                    onChange={handleTypeChange}
                                >
                                    <MenuItem value="color">컬러</MenuItem>
                                    <MenuItem value="image">이미지</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box sx={{ ...COLOR_SWATCH_SX, backgroundColor: normalizeHexColor(params.color) || '#8A8A8A' }} />
                                    <TextField
                                        required
                                        fullWidth
                                        label="대표 컬러"
                                        id="color"
                                        name="color"
                                        value={params.color || ''}
                                        disabled={saving}
                                        onChange={handleUpdateField}
                                        size="small"
                                        inputProps={{ maxLength: 7 }}
                                    />
                                </Box>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="정렬"
                                    id="sort"
                                    name="sort"
                                    type="number"
                                    value={params.sort}
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                    size="small"
                                    inputProps={{ min: 0 }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="상태"
                                    select
                                    id="status"
                                    name="status"
                                    value={params.status}
                                    size="small"
                                    disabled={saving}
                                    onChange={handleUpdateField}
                                >
                                    <MenuItem value={0}>사용</MenuItem>
                                    <MenuItem value={1}>미사용</MenuItem>
                                </TextField>
                            </FormControl>
                        </Grid>
                        {params.type === 'image' && (
                            <>
                                <Grid item xs={12}>
                                    <PreviewImagePanel
                                        previewImageSrc={previewImageSrc}
                                        previewImageFile={params.previewImage}
                                        previewImageUrl={params.previewImageUrl}
                                        disabled={saving}
                                        onUpload={handleUploadClick}
                                        onClearFile={handleClearSelectedImage}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <FormControl fullWidth>
                                        <TextField
                                            required
                                            label="판매 포인트"
                                            id="pricePoint"
                                            name="pricePoint"
                                            type="number"
                                            value={params.pricePoint}
                                            size="small"
                                            disabled={saving}
                                            onChange={handleUpdateField}
                                            inputProps={{ min: 0 }}
                                        />
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                    </Grid>
                    <BottomButtonGroup
                        path={LIST_PATH}
                        search={listSearch}
                        visible={true}
                        curPage={curPage}
                        searchText={searchText}
                        searchSelect={searchSelect}
                        submitDisabled={saving}
                        submitLabel={saving ? '저장 중...' : '저장'}
                        onBackClick={handleBackClick}
                    />
                </Box>
                {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
            </Paper>
        </Grid>
    )
}
